import { isCancel, select, spinner, text } from "@clack/prompts";
import { spawnSync } from "node:child_process";
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import { slugify } from "./config.js";
import {
	detectPackageManager as detectInstalledPackageManager,
	outputTail,
	pmInstallHint,
	type PackageManager,
} from "./scaffold.js";

export type NewAppType = "b2c_app" | "website";

export type ErrorOptions = {
	code: string;
	message: string;
	retryable?: boolean;
	exitCode?: number;
	cta?: {
		description?: string;
		commands: { command: string; description?: string }[];
	};
};

/**
 * incur's `c.error()` builds a result that only takes effect when returned
 * from the command's own `run` — so helper functions abort by throwing this,
 * and each command converts it back into `c.error(...)` at the top level.
 */
export class CommandAbort extends Error {
	constructor(readonly options: ErrorOptions) {
		super(options.message);
	}
}

export type CommandContext = {
	agent: boolean;
};

export function log(c: CommandContext, message: string): void {
	if (!c.agent) console.log(message);
}

/**
 * Names the stage a long command has reached, on stderr.
 *
 * Human progress is a spinner, which writes nothing when stdout is not a TTY —
 * so a machine consumer of `--format json` previously saw a single silent gap
 * from the first build line to the final envelope. This is the one signal that
 * survives being piped, and it goes to stderr so the envelope on stdout stays
 * pure JSON.
 */
export const PHASE_MARKER = "[whop:phase]";

/**
 * Stamped with the wall clock, so a consumer reading the log after the fact can
 * still tell how long each stage took. Without it, progress can only be
 * reconstructed by something watching the stream live — and anything that
 * restarts mid-deploy loses the timings for good.
 */
export function phase(name: string): void {
	process.stderr.write(`${PHASE_MARKER} ${name} t=${Date.now()}\n`);
}

export function cancelled(): never {
	throw new CommandAbort({
		code: "CANCELLED",
		message: "Cancelled.",
		exitCode: 130,
	});
}

export async function promptText(
	message: string,
	placeholder: string,
	initialValue?: string,
): Promise<string | null> {
	const value = await text({ message, placeholder, initialValue });
	if (isCancel(value)) return null;
	return String(value);
}

export async function promptSelect(
	message: string,
	options: { value: string; label: string }[],
): Promise<string> {
	const choice = await select({ message, options });
	if (isCancel(choice)) cancelled();
	return String(choice);
}

export async function promptForAppType(): Promise<NewAppType> {
	const choice = await promptSelect("What are you building?", [
		{ value: "b2c_app", label: "An app — creators install it into their whop" },
		{ value: "website", label: "A website — visitors browse it at your route" },
	]);
	return choice as NewAppType;
}

export async function promptForNewApp(knownAppType?: NewAppType): Promise<{
	name: string;
	route: string;
	appType: NewAppType;
}> {
	const appType = knownAppType ?? (await promptForAppType());

	const name = await promptText("What is your app called?", "My Site");
	if (name === null) cancelled();

	const slug = slugify(name);
	const routeInput = await promptText(
		"Which route should it live at? (your-route.whop.site)",
		slug,
		slug,
	);
	if (routeInput === null) cancelled();
	const route = slugify(routeInput);
	if (!route) {
		throw new CommandAbort({
			code: "ROUTE_REQUIRED",
			message: `Couldn't derive a route from "${routeInput}" — use letters and numbers (e.g. my-site).`,
			retryable: true,
		});
	}
	return { name, route, appType };
}

/**
 * A package manager must exist before anything else — scaffolding,
 * installing, and building all need one.
 */
export function ensurePackageManager(): PackageManager {
	const pm = detectInstalledPackageManager();
	if (pm) return pm;
	throw new CommandAbort({
		code: "NO_PACKAGE_MANAGER",
		message: `No package manager found (looked for bun, pnpm, npm). Install one and retry:\n\n  bun (recommended): ${pmInstallHint("bun")}\n  pnpm:              ${pmInstallHint("pnpm")}\n  npm:               ${pmInstallHint("npm")}`,
	});
}

// npm/pnpm/yarn are .cmd shims on Windows, which can't be spawned without a
// shell. Everywhere else, spawning without a shell avoids quoting pitfalls.
export const SPAWN_VIA_SHELL = process.platform === "win32";

/**
 * The project's lockfile decides the package manager; without one (e.g. a
 * fresh scaffold that hasn't installed yet) fall back to whatever is
 * installed, preferring bun — never blindly npm.
 */
export function detectProjectPackageManager(projectDir: string): string {
	if (
		existsSync(join(projectDir, "bun.lock")) ||
		existsSync(join(projectDir, "bun.lockb"))
	)
		return "bun";
	if (existsSync(join(projectDir, "pnpm-lock.yaml"))) return "pnpm";
	if (existsSync(join(projectDir, "yarn.lock"))) return "yarn";
	if (existsSync(join(projectDir, "package-lock.json"))) return "npm";
	return detectInstalledPackageManager() ?? "npm";
}

export function hasScript(projectDir: string, script: string): boolean {
	try {
		const pkg = JSON.parse(
			readFileSync(join(projectDir, "package.json"), "utf-8"),
		) as { scripts?: Record<string, string> };
		return Boolean(pkg.scripts?.[script]);
	} catch {
		return false;
	}
}

export function runScript(
	projectDir: string,
	script: string,
	extraEnv: Record<string, string> = {},
	scriptArgs: string[] = [],
): void {
	const pm = detectProjectPackageManager(projectDir);
	// npm needs `--` to hand trailing flags to the script rather than eat them;
	// the others forward everything after the script name as-is.
	const separator = pm === "npm" && scriptArgs.length > 0 ? ["--"] : [];
	const result = spawnSync(pm, ["run", script, ...separator, ...scriptArgs], {
		cwd: projectDir,
		stdio: "inherit",
		shell: SPAWN_VIA_SHELL,
		env: { ...process.env, ...extraEnv },
	});
	if (result.error) {
		throw new Error(
			`Could not run \`${pm} run ${script}\`: ${result.error.message}`,
		);
	}
	if (result.status !== 0) {
		throw new Error(
			`\`${pm} run ${script}\` failed with exit code ${result.status ?? "unknown"}`,
		);
	}
}

export function runInstall(c: CommandContext, projectDir: string): void {
	const pm = detectProjectPackageManager(projectDir);
	const spin = c.agent ? null : spinner();
	spin?.start(`Installing dependencies with ${pm}`);
	const result = spawnSync(pm, ["install"], {
		cwd: projectDir,
		stdio: ["ignore", "pipe", "pipe"],
		encoding: "utf-8",
		shell: SPAWN_VIA_SHELL,
	});
	if (result.error || result.status !== 0) {
		spin?.error(`${pm} install failed`);
		throw new Error(
			`\`${pm} install\` failed${result.status != null ? ` with exit code ${result.status}` : ""} — run it manually inside ${projectDir}, then retry.${outputTail(result)}`,
		);
	}
	spin?.stop(`Installed dependencies with ${pm}`);
}

/** Downloads a source archive and returns its bytes. */
export async function downloadArchive(sourceUrl: string): Promise<Uint8Array> {
	const response = await fetch(sourceUrl);
	if (!response.ok) {
		throw new Error(
			`Download failed: ${response.status} ${response.statusText}`,
		);
	}
	return new Uint8Array(await response.arrayBuffer());
}
